import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useCRM } from '../context/CRMContext';
import { db } from '../firebase/config';
import {
  collection, onSnapshot, addDoc, updateDoc, doc, serverTimestamp, query, orderBy,
} from 'firebase/firestore';

// ─── tiny helpers ─────────────────────────────────────────────────────────────
const today     = () => new Date().toISOString().split('T')[0];
const daysUntil = (ds) => ds ? Math.ceil((new Date(ds) - new Date()) / 86400000) : null;
const timeAgo   = (ts) => {
  if (!ts?.toDate) return '';
  const s = Math.floor((Date.now() - ts.toDate()) / 1000);
  if (s < 60)   return 'just now';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400)return Math.floor(s / 3600) + 'h ago';
  return Math.floor(s / 86400) + 'd ago';
};
const avatar = (name, size = 36) => {
  const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const colors = ['#7c3aed','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4'];
  const color  = colors[(name || '').charCodeAt(0) % colors.length];
  return { initials, color, size };
};

const PLATFORM_ICONS = { google_meet:'🎥', teams:'🟦', zoom:'🔵', whatsapp:'📞', phone:'☎️', in_person:'🤝', other:'📅' };
const APP_STAGE_COLORS = { shortlisted:'#64748b', documents:'#f59e0b', applied:'#0ea5e9', conditional:'#8b5cf6', offer:'#10b981', accepted:'#059669', visa:'#7c3aed', visa_granted:'#4f46e5', enrolled:'#0f172a', rejected:'#ef4444', withdrawn:'#94a3b8' };
const APP_STAGE_LABELS = { shortlisted:'Shortlisted', documents:'Docs Ready', applied:'Applied', conditional:'Conditional Offer', offer:'Offer Received', accepted:'Accepted', visa:'Visa Applied', visa_granted:'Visa Granted', enrolled:'Enrolled 🎓', rejected:'Rejected', withdrawn:'Withdrawn' };

// ─── Avatar ───────────────────────────────────────────────────────────────────
function Avatar({ name, size = 36, style = {} }) {
  const a = avatar(name, size);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `linear-gradient(135deg, ${a.color}, ${a.color}bb)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700, color: '#fff', flexShrink: 0,
      boxShadow: `0 2px 8px ${a.color}40`, ...style,
    }}>{a.initials}</div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ icon, title, count, color = '#7c3aed', action, actionLabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <div style={{ width: 32, height: 32, borderRadius: 9, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
        {count !== undefined && (
          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, background: color + '18', color, borderRadius: 10, padding: '2px 7px' }}>{count}</span>
        )}
      </div>
      {action && (
        <button onClick={action} style={{ fontSize: 11, color, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>{actionLabel}</button>
      )}
    </div>
  );
}

// ─── Empty Slice ──────────────────────────────────────────────────────────────
function EmptySlice({ icon, message }) {
  return (
    <div style={{ padding: '18px 14px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 12, color: 'var(--text-secondary)', fontSize: 12 }}>
      <span style={{ fontSize: 22, display: 'block', marginBottom: 6 }}>{icon}</span>
      {message}
    </div>
  );
}

// ─── Meeting Card ─────────────────────────────────────────────────────────────
function MeetingCard({ meeting, lead, onJoin, onMarkDone }) {
  const mins = meeting.time
    ? Math.ceil((new Date(`${meeting.date}T${meeting.time}`) - new Date()) / 60000)
    : null;
  const isNow   = mins !== null && mins <= 15 && mins >= -60;
  const isSoon  = mins !== null && mins > 15 && mins <= 120;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
      background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
      borderLeft: `3px solid ${isNow ? '#ef4444' : isSoon ? '#f59e0b' : '#7c3aed'}`,
      marginBottom: 8,
    }}>
      <div style={{ fontSize: 22, flexShrink: 0 }}>{PLATFORM_ICONS[meeting.platform] || '📅'}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {meeting.title || meeting.meetingType || 'Meeting'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
          👤 {lead?.name || meeting.leadName || '—'}
          {meeting.time && <span style={{ marginLeft: 8 }}>🕐 {meeting.time}</span>}
          {meeting.duration && <span style={{ marginLeft: 8 }}>· {meeting.duration}min</span>}
        </div>
        {isNow && <div style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', marginTop: 3 }}>🔴 Starting now!</div>}
        {isSoon && <div style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', marginTop: 3 }}>⚡ In {mins} minutes</div>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {meeting.meetLink && (
          <a href={meeting.meetLink} target="_blank" rel="noopener noreferrer"
            style={{ padding: '6px 12px', borderRadius: 8, background: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>
            Join
          </a>
        )}
        <button onClick={() => onMarkDone(meeting.id)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'none', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer' }}>✓</button>
      </div>
    </div>
  );
}

// ─── Document Urgency Card ────────────────────────────────────────────────────
function DocUrgencyCard({ lead, docs, onClick }) {
  const total    = docs.length;
  const verified = docs.filter(d => d.status === 'verified').length;
  const pending  = docs.filter(d => d.status === 'uploaded').length;   // needs review
  const rejected = docs.filter(d => d.status === 'rejected').length;
  const pct      = total ? Math.round(verified / total * 100) : 0;
  const urgency  = rejected > 0 ? 'red' : pending > 0 ? 'amber' : pct < 50 ? 'gray' : 'green';
  const urgencyColors = { red:'#ef4444', amber:'#f59e0b', green:'#10b981', gray:'#64748b' };
  const color = urgencyColors[urgency];

  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
      background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
      borderLeft: `3px solid ${color}`, cursor: 'pointer', marginBottom: 8,
      transition: 'box-shadow 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <Avatar name={lead.name} size={36} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{lead.name}</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
          {rejected > 0 && <span style={{ color: '#ef4444', fontWeight: 600 }}>⚠️ {rejected} rejected · </span>}
          {pending > 0  && <span style={{ color: '#f59e0b', fontWeight: 600 }}>📥 {pending} to review · </span>}
          {verified}/{total} verified
        </div>
      </div>
      {/* Progress ring */}
      <svg viewBox="0 0 36 36" style={{ width: 38, height: 38, transform: 'rotate(-90deg)', flexShrink: 0 }}>
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--border)" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${pct} ${100 - pct}`} strokeLinecap="round" />
      </svg>
    </div>
  );
}

// ─── Application Deadline Card ─────────────────────────────────────────────────
function AppDeadlineCard({ app, lead, onClick }) {
  const days  = daysUntil(app.deadline);
  const color = days === null ? '#64748b' : days < 0 ? '#ef4444' : days <= 7 ? '#f59e0b' : '#10b981';
  const stageColor = APP_STAGE_COLORS[app.stage] || '#64748b';

  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
      background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
      borderLeft: `3px solid ${color}`, cursor: 'pointer', marginBottom: 8,
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <Avatar name={lead?.name || app.leadName} size={36} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.universityName}</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
          {lead?.name || app.leadName} · {app.program || 'Program not set'}
        </div>
        <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: stageColor, background: stageColor + '15', padding: '2px 7px', borderRadius: 10 }}>
            {APP_STAGE_LABELS[app.stage] || app.stage}
          </span>
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1 }}>
          {days === null ? '—' : days < 0 ? 'Past' : days === 0 ? 'Today' : days + 'd'}
        </div>
        <div style={{ fontSize: 9, color: 'var(--text-secondary)', marginTop: 2 }}>
          {app.deadline ? new Date(app.deadline).toLocaleDateString('en-GB', { day:'numeric', month:'short' }) : ''}
        </div>
      </div>
    </div>
  );
}

// ─── Student Row (quick overview) ─────────────────────────────────────────────
function StudentRow({ lead, meetings, docs, apps, onOpenStudent }) {
  const todayStr  = today();
  const nextMeet  = meetings.find(m => m.date >= todayStr && m.status === 'scheduled');
  const docsTotal = docs.length;
  const docsVer   = docs.filter(d => d.status === 'verified').length;
  const activeApp = apps.find(a => !['rejected','withdrawn','enrolled'].includes(a.stage));
  const hasAction = docs.some(d => d.status === 'uploaded') || docs.some(d => d.status === 'rejected');

  return (
    <div onClick={() => onOpenStudent(lead)} style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.1s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <Avatar name={lead.name} size={40} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{lead.name}</span>
          {hasAction && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', display: 'inline-block', flexShrink: 0 }} title="Needs attention" />}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
          {nextMeet ? `📅 ${nextMeet.date === todayStr ? 'Today' : nextMeet.date} ${nextMeet.time || ''}` : ''}
          {activeApp && <span style={{ marginLeft: nextMeet ? 8 : 0 }}>🎓 {APP_STAGE_LABELS[activeApp.stage]}</span>}
          {!nextMeet && !activeApp && <span style={{ color: 'var(--text-secondary)' }}>No active engagement</span>}
        </div>
      </div>
      {docsTotal > 0 && (
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: docsVer === docsTotal ? '#10b981' : '#f59e0b' }}>
            {docsVer}/{docsTotal} docs
          </div>
        </div>
      )}
      <span style={{ color: 'var(--text-secondary)', fontSize: 14, flexShrink: 0 }}>›</span>
    </div>
  );
}

// ─── Quick Action Button ──────────────────────────────────────────────────────
function QuickAction({ icon, label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
      padding: '14px 10px', borderRadius: 14, border: '1px solid var(--border)',
      background: 'var(--bg-card)', cursor: 'pointer', flex: 1, transition: 'all 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.background = color + '10'; e.currentTarget.style.borderColor = color + '40'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <span style={{ fontSize: 24 }}>{icon}</span>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
    </button>
  );
}

// ─── Student Detail Panel ──────────────────────────────────────────────────────
function StudentPanel({ lead, meetings, docs, apps, company, currentUser, onClose, setCurrentPage }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [addMeetModal, setAddMeetModal] = useState(false);
  const [addDocModal, setAddDocModal] = useState(false);
  const todayStr = today();

  const upcomingMeetings = meetings.filter(m => m.date >= todayStr && m.status === 'scheduled');
  const verifiedDocs = docs.filter(d => d.status === 'verified').length;
  const activeApps = apps.filter(a => !['rejected','withdrawn'].includes(a.stage));
  const pct = docs.length ? Math.round(verifiedDocs / docs.length * 100) : 0;

  const TABS = [
    { id: 'overview',     label: 'Overview'     },
    { id: 'meetings',     label: `Meetings (${meetings.length})`  },
    { id: 'documents',    label: `Documents (${docs.length})`     },
    { id: 'applications', label: `Applications (${activeApps.length})`},
  ];

  const quickScheduleMeeting = async (platform) => {
    if (!company?.id) return;
    await addDoc(collection(db, 'companies', company.id, 'meetings'), {
      platform, leadId: lead.id, leadName: lead.name,
      date: todayStr, status: 'scheduled',
      meetingType: 'Initial Consultation',
      title: `Meeting with ${lead.name}`,
      createdBy: currentUser?.id, createdByName: currentUser?.name,
      companyId: company.id, createdAt: serverTimestamp(),
    });
  };

  const requestDocs = async (docNames) => {
    if (!company?.id) return;
    for (const name of docNames) {
      await addDoc(collection(db, 'companies', company.id, 'leads', lead.id, 'documents'), {
        name, status: 'pending', leadId: lead.id, companyId: company.id,
        requestedBy: currentUser?.id, createdAt: serverTimestamp(),
      });
    }
  };

  const updateDocStatus = async (docId, status, extra = {}) => {
    await updateDoc(doc(db, 'companies', company.id, 'leads', lead.id, 'documents', docId),
      { status, ...extra, updatedAt: serverTimestamp() });
  };

  const DOC_STATUS_CONFIG = {
    pending:   { label: 'Pending',   color: '#f59e0b', bg: 'rgba(245,158,11,0.1)'  },
    requested: { label: 'Requested', color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)'  },
    uploaded:  { label: 'Uploaded',  color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)'  },
    verified:  { label: 'Verified',  color: '#10b981', bg: 'rgba(16,185,129,0.1)'  },
    rejected:  { label: 'Rejected',  color: '#ef4444', bg: 'rgba(239,68,68,0.1)'   },
  };

  const QUICK_DOC_SETS = [
    { label: '🇬🇧 UK Essentials',   docs: ['Passport (all pages)', 'Grade 12 Marksheet', 'Undergraduate Transcripts', 'IELTS Score Card', 'Statement of Purpose (SOP)', 'LOR – Academic 1', 'Bank Statement (6 months)'] },
    { label: '🇨🇦 Canada Essentials',docs: ['Passport (all pages)', 'Grade 12 Marksheet', 'Undergraduate Transcripts', 'IELTS Score Card', 'Statement of Purpose (SOP)', 'LOR – Academic 1', 'Bank Statement (6 months)', 'Resume / CV'] },
    { label: '🇦🇺 Australia Basics', docs: ['Passport (all pages)', 'Grade 12 Marksheet', 'Undergraduate Transcripts', 'IELTS Score Card', 'Statement of Purpose (SOP)', 'Bank Statement (6 months)'] },
    { label: '🇺🇸 USA Basics',       docs: ['Passport (all pages)', 'Undergraduate Transcripts', 'TOEFL Score Report', 'GRE Score Report', 'Statement of Purpose (SOP)', 'LOR – Academic 1', 'LOR – Academic 2', 'Resume / CV'] },
    { label: '📋 Basic Checklist',    docs: ['Passport (all pages)', 'Grade 12 Marksheet', 'Resume / CV', 'Statement of Purpose (SOP)'] },
  ];

  const existingDocNames = docs.map(d => d.name);

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 480,
      background: 'var(--bg-primary)', borderLeft: '1px solid var(--border)',
      boxShadow: '-12px 0 48px rgba(0,0,0,0.18)', zIndex: 1000,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ padding: '20px 22px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar name={lead.name} size={48} />
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{lead.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{lead.email || lead.phone || 'No contact info'}</div>
              {lead.source && <div style={{ fontSize: 11, marginTop: 3, color: '#7c3aed', fontWeight: 600 }}>📍 {lead.source}</div>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px' }}>×</button>
        </div>

        {/* 3 stat pills */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[
            { icon: '📅', val: upcomingMeetings.length, label: 'meetings',    color: '#7c3aed' },
            { icon: '📁', val: pct + '%',               label: 'docs done',   color: docs.length === 0 ? '#94a3b8' : pct === 100 ? '#10b981' : '#f59e0b' },
            { icon: '🎓', val: activeApps.length,       label: 'applications',color: '#0ea5e9' },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, padding: '8px 6px', background: s.color + '10', borderRadius: 10, textAlign: 'center', border: `1px solid ${s.color}20` }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.val}</div>
              <div style={{ fontSize: 9, color: s.color, fontWeight: 600, marginTop: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, background: 'var(--bg-secondary)', borderRadius: 10, padding: 4 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              flex: 1, padding: '7px 4px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: activeTab === t.id ? 'var(--bg-primary)' : 'transparent',
              color: activeTab === t.id ? '#7c3aed' : 'var(--text-secondary)',
              fontWeight: activeTab === t.id ? 700 : 500, fontSize: 11,
              boxShadow: activeTab === t.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div>
            {/* Quick actions */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, letterSpacing: '0.05em' }}>QUICK ACTIONS</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <QuickAction icon="🎥" label="Google Meet" color="#4285f4" onClick={() => quickScheduleMeeting('google_meet')} />
                <QuickAction icon="🟦" label="Teams" color="#5059c9" onClick={() => quickScheduleMeeting('teams')} />
                <QuickAction icon="🔵" label="Zoom" color="#2d8cff" onClick={() => quickScheduleMeeting('zoom')} />
                <QuickAction icon="🤝" label="In Person" color="#f59e0b" onClick={() => quickScheduleMeeting('in_person')} />
              </div>
            </div>

            {/* Next meeting */}
            {upcomingMeetings.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, letterSpacing: '0.05em' }}>NEXT MEETING</div>
                {upcomingMeetings.slice(0, 1).map(m => (
                  <div key={m.id} style={{ padding: '12px 14px', background: 'rgba(124,58,237,0.06)', borderRadius: 12, border: '1px solid rgba(124,58,237,0.15)' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontSize: 22 }}>{PLATFORM_ICONS[m.platform] || '📅'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{m.title || m.meetingType}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {m.date === todayStr ? '🟢 Today' : m.date}
                          {m.time && ' · ' + m.time}
                        </div>
                      </div>
                      {m.meetLink && (
                        <a href={m.meetLink} target="_blank" rel="noopener noreferrer"
                          style={{ padding: '6px 12px', borderRadius: 8, background: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>
                          Join
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Document progress */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, letterSpacing: '0.05em' }}>DOCUMENT STATUS</div>
              {docs.length === 0 ? (
                <div style={{ padding: '14px', background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>No documents requested yet. Start with a country pack:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {QUICK_DOC_SETS.map(s => (
                      <button key={s.label} onClick={() => requestDocs(s.docs)} style={{
                        padding: '5px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                        border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)', fontWeight: 600,
                      }}>{s.label}</button>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden', marginBottom: 6 }}>
                    <div style={{ height: '100%', width: pct + '%', background: pct === 100 ? '#10b981' : pct > 60 ? '#7c3aed' : '#f59e0b', borderRadius: 4, transition: 'width 0.4s' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                    <span>{verifiedDocs}/{docs.length} verified</span>
                    <span style={{ fontWeight: 700, color: pct === 100 ? '#10b981' : '#f59e0b' }}>{pct}%</span>
                  </div>
                  {/* Docs needing attention */}
                  {docs.filter(d => d.status === 'uploaded' || d.status === 'rejected').map(d => (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--bg-card)', borderRadius: 9, border: '1px solid var(--border)', marginBottom: 6 }}>
                      <span style={{ fontSize: 14 }}>{d.status === 'uploaded' ? '📥' : '⚠️'}</span>
                      <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)' }}>{d.name}</span>
                      {d.status === 'uploaded' && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => updateDocStatus(d.id, 'verified', { verifiedAt: serverTimestamp(), verifiedBy: currentUser?.id })}
                            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #10b981', background: 'rgba(16,185,129,0.08)', color: '#10b981', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>✓ Verify</button>
                          <button onClick={async () => { const r = window.prompt("Rejection reason:"); if (r !== null) updateDocStatus(d.id, 'rejected', { remark: r }); }}
                            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ef4444', background: 'rgba(239,68,68,0.08)', color: '#ef4444', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>✗</button>
                        </div>
                      )}
                      {d.status === 'rejected' && <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>Rejected</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Active applications */}
            {activeApps.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, letterSpacing: '0.05em' }}>APPLICATIONS</div>
                {activeApps.slice(0, 3).map(app => {
                  const sc = APP_STAGE_COLORS[app.stage] || '#64748b';
                  const days = daysUntil(app.deadline);
                  return (
                    <div key={app.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.universityName}</div>
                        <div style={{ marginTop: 3 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: sc, background: sc + '15', padding: '2px 7px', borderRadius: 10 }}>
                            {APP_STAGE_LABELS[app.stage] || app.stage}
                          </span>
                        </div>
                      </div>
                      {days !== null && <div style={{ fontSize: 13, fontWeight: 800, color: days < 0 ? '#ef4444' : days <= 7 ? '#f59e0b' : '#64748b' }}>
                        {days < 0 ? 'Past' : days + 'd'}
                      </div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── MEETINGS TAB ── */}
        {activeTab === 'meetings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
              <button onClick={() => setCurrentPage('meetings')} className="btn-primary" style={{ fontSize: 12, padding: '7px 14px' }}>+ Schedule Meeting</button>
            </div>
            {meetings.length === 0 ? <EmptySlice icon="📅" message="No meetings yet — use quick actions in Overview to schedule one" />
              : meetings.map(m => {
                const sc = m.status === 'completed' ? '#10b981' : m.status === 'cancelled' ? '#ef4444' : '#7c3aed';
                return (
                  <div key={m.id} style={{ padding: '12px 14px', background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 8, borderLeft: `3px solid ${sc}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18 }}>{PLATFORM_ICONS[m.platform] || '📅'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{m.title || m.meetingType || 'Meeting'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {m.date} {m.time && '· ' + m.time} · {m.duration || 30}min
                        </div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: sc, background: sc + '15', padding: '3px 8px', borderRadius: 10 }}>
                        {m.status || 'scheduled'}
                      </span>
                    </div>
                    {m.meetLink && (
                      <a href={m.meetLink} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>
                        🔗 Join Meeting
                      </a>
                    )}
                  </div>
                );
              })
            }
          </div>
        )}

        {/* ── DOCUMENTS TAB ── */}
        {activeTab === 'documents' && (
          <div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>Request a country document pack or go to full Documents page:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {QUICK_DOC_SETS.map(s => {
                  const newDocs = s.docs.filter(n => !existingDocNames.includes(n));
                  return (
                    <button key={s.label} onClick={() => newDocs.length && requestDocs(newDocs)} disabled={!newDocs.length} style={{
                      padding: '5px 10px', borderRadius: 20, fontSize: 11, cursor: newDocs.length ? 'pointer' : 'default',
                      border: '1px solid var(--border)', background: newDocs.length ? 'var(--bg-secondary)' : 'var(--border)',
                      color: newDocs.length ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: 600,
                    }}>{s.label}{!newDocs.length ? ' ✓' : ` +${newDocs.length}`}</button>
                  );
                })}
                <button onClick={() => setCurrentPage('documents')} style={{
                  padding: '5px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                  border: '1px solid #7c3aed', background: 'rgba(124,58,237,0.08)', color: '#7c3aed', fontWeight: 600,
                }}>⊕ Manage all docs →</button>
              </div>
            </div>
            {docs.length === 0 ? <EmptySlice icon="📋" message="No documents requested yet — add a country pack above" />
              : docs.map(d => {
                const sc = DOC_STATUS_CONFIG[d.status] || DOC_STATUS_CONFIG.pending;
                return (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 6 }}>
                    <span style={{ fontSize: 16 }}>{d.status === 'verified' ? '✅' : d.status === 'uploaded' ? '📥' : d.status === 'rejected' ? '❌' : '📋'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                      {d.fileUrl && <a href={d.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: '#7c3aed' }}>📎 View file</a>}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: sc.color, background: sc.bg, padding: '2px 7px', borderRadius: 10 }}>{sc.label}</span>
                    {d.status === 'uploaded' && (
                      <button onClick={() => updateDocStatus(d.id, 'verified', { verifiedAt: serverTimestamp() })}
                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #10b981', background: 'rgba(16,185,129,0.08)', color: '#10b981', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>✓</button>
                    )}
                  </div>
                );
              })
            }
          </div>
        )}

        {/* ── APPLICATIONS TAB ── */}
        {activeTab === 'applications' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
              <button onClick={() => setCurrentPage('applications')} className="btn-primary" style={{ fontSize: 12, padding: '7px 14px' }}>+ Add Application</button>
            </div>
            {apps.length === 0 ? <EmptySlice icon="🎓" message="No applications yet — go to Applications to add universities" />
              : apps.map(app => {
                const sc = APP_STAGE_COLORS[app.stage] || '#64748b';
                const days = daysUntil(app.deadline);
                const stageIdx = ['shortlisted','documents','applied','conditional','offer','accepted','visa','visa_granted','enrolled'].indexOf(app.stage);
                const pct = stageIdx >= 0 ? Math.round(stageIdx / 8 * 100) : 0;
                return (
                  <div key={app.id} style={{ padding: '14px', background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 10, borderTop: `3px solid ${sc}` }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{app.universityName}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{app.country} · {app.program || '—'}</div>
                      </div>
                      {days !== null && <div style={{ fontSize: 14, fontWeight: 800, color: days < 0 ? '#ef4444' : days <= 7 ? '#f59e0b' : '#64748b', flexShrink: 0, marginLeft: 8 }}>
                        {days < 0 ? 'Past' : days + 'd left'}
                      </div>}
                    </div>
                    <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
                      <div style={{ height: '100%', width: pct + '%', background: sc, borderRadius: 2 }} />
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: sc, background: sc + '15', padding: '2px 8px', borderRadius: 10 }}>
                      {APP_STAGE_LABELS[app.stage] || app.stage}
                    </span>
                  </div>
                );
              })
            }
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Engagement Page ──────────────────────────────────────────────────────
export default function EngagementPage() {
  const { currentUser, company, leads, setCurrentPage } = useCRM();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  const [allMeetings, setAllMeetings]     = useState([]);
  const [allDocsByLead, setAllDocsByLead] = useState({});   // { leadId: [docs] }
  const [allAppsByLead, setAllAppsByLead] = useState({});   // { leadId: [apps] }
  const [selectedLead, setSelectedLead]   = useState(null);
  const [search, setSearch]               = useState('');
  const [studentsPage, setStudentsPage]   = useState(0);
  const STUDENTS_PER_PAGE = 20;

  const visibleLeads = useMemo(() => {
    if (!leads) return [];
    const base = isAdmin ? leads : leads.filter(l =>
      l.assignedTo === currentUser?.id || l.assignedTo === currentUser?.name || l.assignedToId === currentUser?.id
    );
    if (!search) return base;
    const q = search.toLowerCase();
    return base.filter(l => (l.name || '').toLowerCase().includes(q) || (l.email || '').toLowerCase().includes(q));
  }, [leads, isAdmin, currentUser, search]);

  // Load all meetings
  useEffect(() => {
    if (!company?.id) return;
    return onSnapshot(collection(db, 'companies', company.id, 'meetings'), snap => {
      setAllMeetings(snap.docs.map(d => ({ ...d.data(), id: d.id })));
    });
  }, [company?.id]);

  // Load docs + apps for visible leads (up to 40)
  useEffect(() => {
    if (!company?.id || !visibleLeads.length) return;
    const unsubDocs = visibleLeads.slice(0, 40).map(lead =>
      onSnapshot(collection(db, 'companies', company.id, 'leads', lead.id, 'documents'), snap => {
        setAllDocsByLead(s => ({ ...s, [lead.id]: snap.docs.map(d => ({ ...d.data(), id: d.id })) }));
      })
    );
    const unsubApps = visibleLeads.slice(0, 40).map(lead =>
      onSnapshot(collection(db, 'companies', company.id, 'leads', lead.id, 'applications'), snap => {
        setAllAppsByLead(s => ({ ...s, [lead.id]: snap.docs.map(d => ({ ...d.data(), id: d.id })) }));
      })
    );
    return () => { unsubDocs.forEach(u => u()); unsubApps.forEach(u => u()); };
  }, [visibleLeads.length, company?.id]); // eslint-disable-line

  const todayStr = today();

  // ── Computed attention lists ──────────────────────────────────────────────
  const todayMeetings = useMemo(() =>
    allMeetings.filter(m => m.date === todayStr && m.status === 'scheduled')
      .sort((a,b) => (a.time||'').localeCompare(b.time||''))
  , [allMeetings, todayStr]);

  const upcomingMeetings = useMemo(() =>
    allMeetings.filter(m => m.date > todayStr && m.status === 'scheduled')
      .sort((a,b) => a.date.localeCompare(b.date)).slice(0, 8)
  , [allMeetings, todayStr]);

  const docsNeedingAttention = useMemo(() => {
    return visibleLeads
      .map(lead => {
        const docs = allDocsByLead[lead.id] || [];
        const hasUploaded = docs.some(d => d.status === 'uploaded');
        const hasRejected = docs.some(d => d.status === 'rejected');
        return { lead, docs, hasUploaded, hasRejected, score: (hasRejected ? 2 : 0) + (hasUploaded ? 1 : 0) };
      })
      .filter(x => x.score > 0)
      .sort((a,b) => b.score - a.score)
      .slice(0, 10);
  }, [visibleLeads, allDocsByLead]);

  const upcomingDeadlines = useMemo(() => {
    const deadlines = [];
    visibleLeads.forEach(lead => {
      (allAppsByLead[lead.id] || []).forEach(app => {
        if (!app.deadline) return;
        const days = daysUntil(app.deadline);
        if (days !== null && days >= -3 && days <= 30) {
          deadlines.push({ app, lead, days });
        }
      });
    });
    return deadlines.sort((a,b) => a.days - b.days).slice(0, 8);
  }, [visibleLeads, allAppsByLead]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const totalDocs = Object.values(allDocsByLead).flat();
    const totalApps = Object.values(allAppsByLead).flat();
    return {
      meetingsToday:   todayMeetings.length,
      docsToReview:    totalDocs.filter(d => d.status === 'uploaded').length,
      activeApps:      totalApps.filter(a => !['rejected','withdrawn','enrolled'].includes(a.stage)).length,
      enrollments:     totalApps.filter(a => a.stage === 'enrolled').length,
    };
  }, [todayMeetings, allDocsByLead, allAppsByLead]);

  const markMeetingDone = async (id) => {
    await updateDoc(doc(db, 'companies', company.id, 'meetings', id), { status: 'completed', updatedAt: serverTimestamp() });
  };

  const getLeadMeetings = useCallback((lead) =>
    allMeetings.filter(m => m.leadId === lead.id)
      .sort((a,b) => b.date.localeCompare(a.date))
  , [allMeetings]);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>

      {/* ── Main scrollable content ────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', background: 'var(--bg-primary)' }}>

        {/* Greeting */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
            {new Date().getHours() < 12 ? '🌤 Good morning' : new Date().getHours() < 17 ? '☀️ Good afternoon' : '🌙 Good evening'}, {currentUser?.name?.split(' ')[0] || 'there'}
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            {todayMeetings.length > 0
              ? `You have ${todayMeetings.length} meeting${todayMeetings.length > 1 ? 's' : ''} today.`
              : "Here's your student engagement overview."
            }
            {stats.docsToReview > 0 && ` · ${stats.docsToReview} document${stats.docsToReview > 1 ? 's' : ''} waiting for review.`}
          </p>
        </div>

        {/* ── 4 stat cards ──────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
          {[
            { icon: '📅', label: "Today's Meetings",  val: stats.meetingsToday, color: '#7c3aed', bg: 'rgba(124,58,237,0.07)' },
            { icon: '📥', label: 'Docs to Review',    val: stats.docsToReview,  color: '#f59e0b', bg: 'rgba(245,158,11,0.07)'  },
            { icon: '🎓', label: 'Active Applications',val: stats.activeApps,   color: '#0ea5e9', bg: 'rgba(14,165,233,0.07)'  },
            { icon: '🏆', label: 'Enrolled',           val: stats.enrollments,  color: '#10b981', bg: 'rgba(16,185,129,0.07)'  },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}20`, borderRadius: 16, padding: '18px 20px', boxShadow: 'var(--shadow-card)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 20 }}>{s.icon}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: s.color }}>{s.label}</span>
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* ── 3 columns ─────────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 28 }}>

          {/* Today's meetings */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 18 }}>
            <SectionHeader icon="📅" title="Today's Meetings" count={todayMeetings.length} color="#7c3aed"
              action={() => setCurrentPage('meetings')} actionLabel="All meetings →" />
            {todayMeetings.length === 0
              ? <EmptySlice icon="✨" message="No meetings today — enjoy the clarity!" />
              : todayMeetings.map(m => (
                <MeetingCard key={m.id} meeting={m}
                  lead={visibleLeads.find(l => l.id === m.leadId)}
                  onMarkDone={markMeetingDone}
                  onJoin={() => m.meetLink && window.open(m.meetLink, '_blank')}
                />
              ))
            }
            {upcomingMeetings.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, letterSpacing: '0.05em' }}>UPCOMING</div>
                {upcomingMeetings.slice(0, 4).map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 14 }}>{PLATFORM_ICONS[m.platform] || '📅'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {visibleLeads.find(l => l.id === m.leadId)?.name || m.leadName || '—'}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{m.date} {m.time && '· ' + m.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Documents needing review */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 18 }}>
            <SectionHeader icon="📥" title="Docs Needing Review" count={docsNeedingAttention.length} color="#f59e0b"
              action={() => setCurrentPage('documents')} actionLabel="All documents →" />
            {docsNeedingAttention.length === 0
              ? <EmptySlice icon="🎉" message="All documents are up to date!" />
              : docsNeedingAttention.map(({ lead, docs }) => (
                <DocUrgencyCard key={lead.id} lead={lead} docs={docs} onClick={() => setSelectedLead(lead)} />
              ))
            }
          </div>

          {/* Application deadlines */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 18 }}>
            <SectionHeader icon="⏰" title="Upcoming Deadlines" count={upcomingDeadlines.length} color="#ef4444"
              action={() => setCurrentPage('applications')} actionLabel="All applications →" />
            {upcomingDeadlines.length === 0
              ? <EmptySlice icon="📆" message="No deadlines in the next 30 days" />
              : upcomingDeadlines.map(({ app, lead, days }) => (
                <AppDeadlineCard key={app.id + lead.id} app={app} lead={lead} onClick={() => setSelectedLead(lead)} />
              ))
            }
          </div>
        </div>

        {/* ── Student list ────────────────────────────────────────────────────── */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <SectionHeader icon="👥" title="All Students" count={visibleLeads.filter(l => l.name?.trim()).length} color="#0ea5e9" />
            <input className="crm-input" placeholder="Search students…" value={search}
              onChange={e => { setSearch(e.target.value); setStudentsPage(0); }} style={{ marginLeft: 'auto', width: 200, fontSize: 12 }} />
          </div>
          {(() => {
            const searchQ = search.toLowerCase();
            const allNamed = visibleLeads.filter(l => l.name?.trim());
            const filtered = searchQ
              ? allNamed.filter(l => l.name.toLowerCase().includes(searchQ) || (l.email || '').toLowerCase().includes(searchQ))
              : allNamed;
            const totalPgs = Math.ceil(filtered.length / STUDENTS_PER_PAGE);
            const paged = filtered.slice(studentsPage * STUDENTS_PER_PAGE, (studentsPage + 1) * STUDENTS_PER_PAGE);
            if (filtered.length === 0) return (
              <div style={{ padding: 30, textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>
                {search ? 'No students match your search' : 'No students found'}
              </div>
            );
            return (
              <>
                {paged.map(lead => (
                  <StudentRow key={lead.id} lead={lead}
                    meetings={getLeadMeetings(lead)}
                    docs={allDocsByLead[lead.id] || []}
                    apps={allAppsByLead[lead.id] || []}
                    onOpenStudent={setSelectedLead}
                  />
                ))}
                {totalPgs > 1 && (
                  <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <button onClick={() => setStudentsPage(p => Math.max(0, p - 1))} disabled={studentsPage === 0} style={{
                      padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'none',
                      cursor: studentsPage === 0 ? 'not-allowed' : 'pointer', fontSize: 12,
                      color: studentsPage === 0 ? 'var(--text-secondary)' : 'var(--text-primary)', fontWeight: 600,
                    }}>← Previous</button>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      Page {studentsPage + 1} of {totalPgs} &nbsp;·&nbsp; {filtered.length} students
                    </span>
                    <button onClick={() => setStudentsPage(p => Math.min(totalPgs - 1, p + 1))} disabled={studentsPage >= totalPgs - 1} style={{
                      padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'none',
                      cursor: studentsPage >= totalPgs - 1 ? 'not-allowed' : 'pointer', fontSize: 12,
                      color: studentsPage >= totalPgs - 1 ? 'var(--text-secondary)' : 'var(--text-primary)', fontWeight: 600,
                    }}>Next →</button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* ── Student Detail Panel ────────────────────────────────────────────── */}
      {selectedLead && (
        <StudentPanel
          lead={selectedLead}
          meetings={getLeadMeetings(selectedLead)}
          docs={allDocsByLead[selectedLead.id] || []}
          apps={allAppsByLead[selectedLead.id] || []}
          company={company}
          currentUser={currentUser}
          setCurrentPage={setCurrentPage}
          onClose={() => setSelectedLead(null)}
        />
      )}
    </div>
  );
}
